/**
 * Service d'authentification pour les patients
 * Gère les tokens temporaires, la session et l'Apollo Client dédié
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import {
  PATIENT_AUTH_CONFIG,
  PATIENT_ENDPOINTS,
  SECURITY_CONFIG,
  ENVIRONMENT,
} from '@/config/constants';
import {
  PatientTokenData,
  PatientSession,
  PatientFormData,
  PatientFormValidator,
  PatientSessionManager,
} from '@/config/patient-auth';

/**
 * Interface pour le retour des opérations de validation
 */
interface TokenValidationResult {
  isValid: boolean;
  error?: string;
  tokenData?: PatientTokenData;
}

/**
 * Interface pour le retour des opérations de sauvegarde
 */
interface SaveResult {
  success: boolean;
  error?: string;
  savedAt?: string;
}

/**
 * Service singleton pour la gestion de l'authentification patient
 */
class PatientAuthService {
  private static instance: PatientAuthService;

  private apolloClient: ApolloClient<any> | null = null;

  private autoSaveTimer: NodeJS.Timeout | null = null;

  private rateLimitTracker: Map<string, number> = new Map();

  private constructor() {
    if (ENVIRONMENT.enableDebugLogs) {
      console.log('[PatientAuthService] Instance créée');
    }
  }

  static getInstance(): PatientAuthService {
    if (!PatientAuthService.instance) {
      PatientAuthService.instance = new PatientAuthService();
    }
    return PatientAuthService.instance;
  }

  /**
   * Sauvegarde les données du token patient
   */
  async saveToken(tokenData: PatientTokenData): Promise<void> {
    try {
      const dataToStore = {
        ...tokenData,
        savedAt: new Date().toISOString(),
      };

      await AsyncStorage.setItem(
        PATIENT_AUTH_CONFIG.STORAGE_KEYS.PATIENT_TOKEN,
        JSON.stringify(dataToStore)
      );

      if (ENVIRONMENT.enableDebugLogs) {
        console.log(
          '[PatientAuthService] Token sauvegardé:',
          tokenData.token.substring(0, 8) + '...'
        );
      }
    } catch (error) {
      console.error('[PatientAuthService] Erreur lors de la sauvegarde du token:', error);
      throw new Error(PATIENT_AUTH_CONFIG.ERROR_MESSAGES.SAVE_ERROR);
    }
  }

  /**
   * Récupère les données du token patient
   */
  async getToken(): Promise<PatientTokenData | null> {
    try {
      const data = await AsyncStorage.getItem(PATIENT_AUTH_CONFIG.STORAGE_KEYS.PATIENT_TOKEN);
      if (!data) return null;

      const tokenData: PatientTokenData = JSON.parse(data);

      // Vérifier la validité du token
      const validationResult = await this.validateStoredToken(tokenData);
      if (!validationResult.isValid) {
        await this.clearToken();
        return null;
      }

      return tokenData;
    } catch (error) {
      console.error('[PatientAuthService] Erreur lors de la récupération du token:', error);
      return null;
    }
  }

  /**
   * Récupère les données du token patient SANS validation
   * Utilisé pour créer le client Apollo avant la validation complète
   */
  async getTokenRaw(): Promise<PatientTokenData | null> {
    try {
      const data = await AsyncStorage.getItem(PATIENT_AUTH_CONFIG.STORAGE_KEYS.PATIENT_TOKEN);
      if (!data) return null;

      return JSON.parse(data);
    } catch (error) {
      console.error('[PatientAuthService] Erreur lors de la récupération du token:', error);
      return null;
    }
  }

  /**
   * Supprime le token et nettoie la session
   */
  async clearToken(): Promise<void> {
    try {
      await AsyncStorage.multiRemove([
        PATIENT_AUTH_CONFIG.STORAGE_KEYS.PATIENT_TOKEN,
        PATIENT_AUTH_CONFIG.STORAGE_KEYS.PATIENT_DATA,
        PATIENT_AUTH_CONFIG.STORAGE_KEYS.PATIENT_SESSION,
      ]);

      this.apolloClient = null;
      this.stopAutoSave();

      if (ENVIRONMENT.enableDebugLogs) {
        console.log('[PatientAuthService] Session nettoyée');
      }
    } catch (error) {
      console.error('[PatientAuthService] Erreur lors du nettoyage:', error);
    }
  }

  /**
   * Valide un token stocké
   */
  private async validateStoredToken(tokenData: PatientTokenData): Promise<TokenValidationResult> {
    // Vérifier le format du token
    if (!PatientFormValidator.validateToken(tokenData.token)) {
      return {
        isValid: false,
        error: PATIENT_AUTH_CONFIG.ERROR_MESSAGES.TOKEN_INVALID,
      };
    }

    // Vérifier l'expiration
    const expiresAt = new Date(tokenData.expiresAt);
    const now = new Date();

    if (expiresAt <= now) {
      return {
        isValid: false,
        error: PATIENT_AUTH_CONFIG.ERROR_MESSAGES.TOKEN_EXPIRED,
      };
    }

    return {
      isValid: true,
      tokenData,
    };
  }

  /**
   * Vérifie si le token est valide
   */
  async isTokenValid(): Promise<boolean> {
    const tokenData = await this.getToken();
    if (!tokenData) return false;

    const validation = await this.validateStoredToken(tokenData);
    return validation.isValid;
  }

  /**
   * Vérifie si le token expire bientôt et peut être étendu
   */
  async shouldExtendToken(): Promise<boolean> {
    const tokenData = await this.getToken();
    if (!tokenData) return false;

    const expiresAt = new Date(tokenData.expiresAt);
    const now = new Date();
    const hoursRemaining = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60);

    return hoursRemaining <= PATIENT_AUTH_CONFIG.TOKEN.AUTO_EXTEND_THRESHOLD;
  }

  /**
   * Crée et configure l'Apollo Client pour les patients
   * Le client est recréé à chaque fois pour utiliser le token le plus récent
   */
  getApolloClient(token: string | null, forceNew: boolean = false): ApolloClient<any> {
    // Toujours recréer le client si forceNew est true ou si le token a changé
    if (forceNew || !this.apolloClient) {
      const httpLink = createHttpLink({
        uri: PATIENT_ENDPOINTS.HASURA_URL,
      });

      const authLink = setContext((_, { headers }) => {
        const authHeaders: Record<string, string> = {
          ...headers,
          'x-hasura-role': PATIENT_AUTH_CONFIG.APOLLO.ROLE,
        };

        // Ajouter le token seulement s'il existe
        if (token) {
          authHeaders[PATIENT_AUTH_CONFIG.APOLLO.HEADER_KEY] = token;
        }

        // Ajouter les headers de sécurité en développement
        if (ENVIRONMENT.isDevelopment) {
          Object.assign(authHeaders, SECURITY_CONFIG.SECURITY_HEADERS);
        }

        return {
          headers: authHeaders,
        };
      });

      const cache = new InMemoryCache({
        typePolicies: {
          patient_temp_tokens: {
            keyFields: ['token'],
            fields: {
              form_data: {
                merge: (existing: Record<string, unknown> = {}, incoming: Record<string, unknown> = {}) => {
                  return {
                    ...existing,
                    ...incoming,
                  };
                },
              },
            },
          },
          patient: {
            keyFields: ['id'],
          },
        },
      });

      this.apolloClient = new ApolloClient({
        link: authLink.concat(httpLink),
        cache,
        defaultOptions: {
          watchQuery: {
            fetchPolicy: PATIENT_AUTH_CONFIG.APOLLO.FETCH_POLICY,
          },
          query: {
            fetchPolicy: PATIENT_AUTH_CONFIG.APOLLO.FETCH_POLICY,
          },
          mutate: {
            fetchPolicy: 'no-cache',
          },
        },
        name: 'PatientFormClient',
        version: '1.0',
        devtools: {
          enabled: ENVIRONMENT.enableDebugLogs,
          name: 'Patient Form Apollo',
        },
      });

      if (ENVIRONMENT.enableDebugLogs) {
        console.log(
          '[PatientAuthService] Apollo Client créé avec token:',
          token ? token.substring(0, 8) + '...' : 'null'
        );
      }
    }

    return this.apolloClient;
  }

  /**
   * Sauvegarde les données du formulaire
   */
  async saveFormData(data: PatientFormData): Promise<SaveResult> {
    try {
      const dataToStore = {
        ...data,
        metadata: {
          ...data.metadata,
          lastModifiedAt: new Date().toISOString(),
        },
      };

      await AsyncStorage.setItem(
        PATIENT_AUTH_CONFIG.STORAGE_KEYS.PATIENT_DATA,
        JSON.stringify(dataToStore)
      );

      if (ENVIRONMENT.enableDebugLogs) {
        console.log('[PatientAuthService] Données du formulaire sauvegardées');
      }

      return {
        success: true,
        savedAt: dataToStore.metadata.lastModifiedAt,
      };
    } catch (error) {
      console.error('[PatientAuthService] Erreur lors de la sauvegarde:', error);
      return {
        success: false,
        error: PATIENT_AUTH_CONFIG.ERROR_MESSAGES.SAVE_ERROR,
      };
    }
  }

  /**
   * Récupère les données du formulaire
   */
  async getFormData(): Promise<PatientFormData | null> {
    try {
      const data = await AsyncStorage.getItem(PATIENT_AUTH_CONFIG.STORAGE_KEYS.PATIENT_DATA);
      if (!data) return null;

      return JSON.parse(data);
    } catch (error) {
      console.error('[PatientAuthService] Erreur lors de la récupération des données:', error);
      return null;
    }
  }

  /**
   * Démarre l'auto-sauvegarde
   */
  startAutoSave(saveCallback: () => Promise<void>): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }

    this.autoSaveTimer = setInterval(async () => {
      try {
        await saveCallback();
        if (ENVIRONMENT.enableDebugLogs) {
          console.log('[PatientAuthService] Auto-sauvegarde effectuée');
        }
      } catch (error) {
        console.error("[PatientAuthService] Erreur lors de l'auto-sauvegarde:", error);
      }
    }, PATIENT_AUTH_CONFIG.AUTO_SAVE.INTERVAL_MS);

    if (ENVIRONMENT.enableDebugLogs) {
      console.log('[PatientAuthService] Auto-sauvegarde démarrée');
    }
  }

  /**
   * Arrête l'auto-sauvegarde
   */
  stopAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
      if (ENVIRONMENT.enableDebugLogs) {
        console.log('[PatientAuthService] Auto-sauvegarde arrêtée');
      }
    }
  }

  /**
   * Sauvegarde la session patient
   */
  async saveSession(session: PatientSession): Promise<void> {
    try {
      await AsyncStorage.setItem(
        PATIENT_AUTH_CONFIG.STORAGE_KEYS.PATIENT_SESSION,
        JSON.stringify({
          ...session,
          lastAccess: new Date().toISOString(),
        })
      );
    } catch (error) {
      console.error('[PatientAuthService] Erreur lors de la sauvegarde de session:', error);
    }
  }

  /**
   * Récupère la session patient
   */
  async getSession(): Promise<PatientSession | null> {
    try {
      const data = await AsyncStorage.getItem(PATIENT_AUTH_CONFIG.STORAGE_KEYS.PATIENT_SESSION);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('[PatientAuthService] Erreur lors de la récupération de session:', error);
      return null;
    }
  }

  /**
   * Vérifie le rate limiting côté client
   */
  checkRateLimit(identifier: string = 'default'): boolean {
    const now = Date.now();
    const lastRequest = this.rateLimitTracker.get(identifier) || 0;

    if (
      now - lastRequest <
      SECURITY_CONFIG.RATE_LIMIT.BLOCK_DURATION_MS /
        SECURITY_CONFIG.RATE_LIMIT.MAX_REQUESTS_PER_MINUTE
    ) {
      return false; // Rate limited
    }

    this.rateLimitTracker.set(identifier, now);
    return true;
  }

  /**
   * Nettoie le service (appelé lors de la fermeture de l'app)
   */
  cleanup(): void {
    this.stopAutoSave();
    this.apolloClient = null;
    this.rateLimitTracker.clear();

    if (ENVIRONMENT.enableDebugLogs) {
      console.log('[PatientAuthService] Service nettoyé');
    }
  }

  /**
   * Méthode de débogage pour obtenir des informations sur l'état du service
   */
  async getDebugInfo(): Promise<any> {
    if (!ENVIRONMENT.enableDebugLogs) {
      return null;
    }

    const tokenData = await this.getToken();
    const formData = await this.getFormData();
    const session = await this.getSession();

    return {
      hasToken: !!tokenData,
      tokenExpiry: tokenData?.expiresAt,
      hasFormData: !!formData,
      currentStep: formData?.metadata?.currentStep,
      hasApolloClient: !!this.apolloClient,
      autoSaveActive: !!this.autoSaveTimer,
      sessionActive: session?.isActive,
      rateLimitEntries: this.rateLimitTracker.size,
    };
  }
}

// Export du service singleton
export default PatientAuthService.getInstance();

// Export des types et interfaces pour usage externe
export type { TokenValidationResult, SaveResult };

// Export des méthodes utilitaires
export { PatientFormValidator, PatientSessionManager };
